import { useMutation, useQuery } from "@apollo/client";
import dayjs from "dayjs";
import { useCallback, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import { CANCEL_SALE, GENERATE_INVOICE, GENERATE_PACKING_LIST } from "../apollo/mutations";
import { GET_SALES, LIST_PRODUCTS, LIST_USERS_BY_ROLE } from "../apollo/queries";
import { SESSION_STORAGE } from "../common/masterEnums";
import useFilters from "../core/hooks/useFilters";
import { decryptData } from "../utils/encryptData";
import { GENERAL } from "../utils/encryptedConstants.";
import { downloadPDF, getSelectedStoreDetails, printPDF } from "../utils/helperFunctions";
import { showSwal } from "../utils/swal";

const sort_values = [
  {
    value: "total_amount",
    label: "Sort by Total Amount",
  },
];

const useSales = () => {

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedRep, setSelectedRep] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedToDate, setSelectedToDate] = useState(null);
  const [selectedFromDate, setSelectedFromDate] = useState(null);
  const { search, sort, onChange, onChangeSort, pagination, debouncedValue, handlePageChange } = useFilters();

  const { loading, data, refetch } = useQuery(GET_SALES, {
    variables: {
      input: {
        limit: pagination.limit,
        pageStart: pagination.pageStart,
        store_id: getSelectedStoreDetails("id"),
        from_date: selectedFromDate ? dayjs(selectedFromDate).format("YYYY-MM-DD") : null,
        to_date: selectedToDate ? dayjs(selectedToDate).format("YYYY-MM-DD") : null,
        user_id: selectedRep?.value,
        status: selectedStatus?.value,
        name: debouncedValue,
        sort: sort?.value,
        product_id: [selectedProduct?.value],
        customer_id: selectedCustomer?.value,
        brand_id: [selectedBrand?.value]
      },
    },
    fetchPolicy: "network-only",
  });

  const { data: repsData, error: repsError } = useQuery(LIST_USERS_BY_ROLE, {
    variables: {
      role: ["Representative", "Manager"],
    },
    fetchPolicy: "cache-and-network",
  });

  const { data: productsData } = useQuery(LIST_PRODUCTS, {
    variables: {
      storeId: getSelectedStoreDetails("id"),
      brandId: selectedBrand?.value || null,
    },
    fetchPolicy: "cache-and-network",
  });

  if (repsError) {
    console.log("Error While Getting Representatives", repsError);
  }

  const representatives =
    repsData?.listUserByRole?.map((user) => ({
      label: user.full_name,
      value: user.id,
    })) || [];

  const products =
    productsData?.listProduct?.map((item) => ({
      label: item.name,
      value: item.id,
    })) || [];

  const [generateInvoice] = useMutation(GENERATE_INVOICE, {
    onCompleted: (data) => downloadPDF(data?.generateSalePDF?.data?.pdf, `${data?.generateSalePDF?.data?.invoice_no}`),
    onError: (err) => {
      console.error("Error generating PDF:", err.message);
    },
  });

  const [generatePackingList] = useMutation(GENERATE_PACKING_LIST, {
    onCompleted: (data) => printPDF(data?.generatePackingPDF?.data?.pdf),
    onError: (err) => {
      console.error("Error generating PDF:", err.message);
    },
  });

  const [cancelSale] = useMutation(CANCEL_SALE,
    {
      onCompleted: () => {
        showSwal({
          title: "Cancelled!",
          text: "Sale record has been marked as cancelled.",
        })
        refetch()
      }
    }
  );

  const downloadInvoice = useCallback((id) => {

    let payload = {
      variables: {
        generateSalePdfId: id,
      },
    };

    toast.promise(generateInvoice(payload), {
      pending: "Generating Invoice ...",
      success: "Invoice Generated Successfully",
    });

  }, []);

  const downloadPackingList = useCallback((id) => {

    let payload = {
      variables: {
        generatePackingPdfId: id,
      },
    };

    toast.promise(generatePackingList(payload), {
      pending: "Generating Packing List ...",
      success: "Packing List Generated Successfully",
    });

  }, []);

  const onRefresh = useCallback(() => {
    refetch();
  }, []);

  const onCancel = useCallback((cancelSaleId) => {
    showSwal({
      title: "Are you sure you want to cancel?",
      text: "You won't be able to revert this!",
      icon: "question",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      showCancelButton: true,
      onConfirm: () => {
        cancelSale({ variables: { cancelSaleId } })
      }
    })
  }, [])

  return {
    values: {
      products,
      sort_values,
      data: data?.allSales?.data || [],
      total: data?.allSales?.total || 0,
      loading,
      search,
      sort,
      representatives,
      selectedToDate,
      selectedFromDate,
      selectedRep,
      selectedStatus,
      statusList: decryptData(GENERAL, SESSION_STORAGE)?.sale_status,
      pageValues: {
        current: pagination.pageStart + 1,
        pageSize: pagination.limit,
        total: data?.allSales?.total || 0,
      },
      selectedProduct,
      selectedCustomer,
      selectedBrand
    },
    states: {
      setSelectedFromDate,
      setSelectedToDate,
      setSelectedRep,
      setSelectedStatus,
      setSelectedProduct,
      setSelectedCustomer,
      setSelectedBrand
    },
    functions: {
      onChange,
      handlePageChange,
      onRefresh,
      downloadInvoice,
      downloadPackingList,
      onCancel,
      onChangeSort
    },
  };
};

export default useSales;
